import React, { useMemo } from "react";
import { Eye, ArrowUpRight } from "lucide-react";
import { Nav } from "@/components/site/Nav";
import { Footer } from "@/components/site/Footer";
import { LINKS } from "@/lib/links";

const GUIDE_PDF_URL =
  "https://drive.google.com/file/d/1e7EGhXM2xmpQgD6LqEvi-gP1FdqFuDM-/view?usp=sharing";

const STEPS = [
  {
    label: "Book a Call",
    title: "Money Clarity Call",
    body:
      "30 minutes. No pitch, no pressure. A quiet conversation about your goals and whether this approach makes sense for your situation.",
    href: LINKS.calendly,
    cta: "Schedule",
    external: true,
  },
  {
    label: "Explore",
    title: "Visit the Website",
    body: "Learn more about the IBC approach and how it works.",
    href: "/",
    cta: "Visit lizdirocco.com",
    external: false,
  },
  {
    label: "Keep Learning",
    title: "Join the Weekly Letter",
    body:
      "Short, thoughtful weekly notes on financial literacy, cash flow, and the quiet work of building wealth.",
    href: "/#newsletter",
    cta: "Subscribe",
    external: false,
  },
];

export default function ThankYouPage() {
  const firstName = useMemo(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      const n = (p.get("name") || "").trim();
      return n || "";
    } catch {
      return "";
    }
  }, []);

  return (
    <div className="min-h-screen bg-paper text-nearblack font-sans antialiased">
      <meta name="robots" content="noindex,nofollow" />

      <Nav heroVisible={true} />

      {/* Hero / thank you */}
      <section
        data-testid="thank-you-hero"
        className="bg-nearblack text-paper pt-32 md:pt-40 pb-24 md:pb-32"
      >
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 lg:px-16">
          <p className="eyebrow-light mb-8" data-testid="thank-you-eyebrow">
            Thank You
          </p>
          <h1
            data-testid="thank-you-headline"
            className="font-serif text-paper leading-[1.04] text-balance max-w-3xl"
            style={{ fontSize: "clamp(2.2rem, 5.5vw, 4.6rem)", fontWeight: 400 }}
          >
            {firstName ? (
              <>
                Thank you,{" "}
                <em className="italic text-camellight" style={{ fontWeight: 400 }}>
                  {firstName}
                </em>
                .
              </>
            ) : (
              <>
                Your guide is{" "}
                <em className="italic text-camellight" style={{ fontWeight: 400 }}>
                  on its way
                </em>
                .
              </>
            )}
          </h1>
          <p
            data-testid="thank-you-sub"
            className="mt-8 max-w-xl text-paper/85 text-base md:text-lg leading-[1.7] font-light"
          >
            Your copy of <em className="italic">The Infinite Banking Guide</em> is
            ready. Open it in a new tab using the button below.
          </p>

          <div className="mt-12 flex flex-col sm:flex-row gap-4">
            <a
              href={GUIDE_PDF_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="thank-you-download"
              className="btn-camel inline-flex items-center justify-center gap-2 group"
            >
              <Eye className="w-3.5 h-3.5" />
              View the Guide
            </a>
            <a
              href="/"
              data-testid="thank-you-home"
              className="btn-outline-light inline-flex items-center justify-center"
            >
              Back to lizdirocco.com
            </a>
          </div>

          <p
            data-testid="thank-you-letter-note"
            className="mt-10 md:mt-12 text-[13px] text-paper/60 font-light italic font-serif tracking-wide"
            style={{ fontSize: "0.95rem" }}
          >
            New letters go out every week — your first one is on its way.
          </p>
        </div>
      </section>

      {/* Next steps */}
      <section className="bg-paper py-24 md:py-32 border-b border-paperdeep">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 lg:px-16">
          <p className="eyebrow mb-6">Next Steps</p>
          <h2
            className="font-serif text-nearblack leading-[1.08] text-balance max-w-3xl"
            style={{ fontSize: "clamp(1.85rem, 3.4vw, 2.85rem)", fontWeight: 400 }}
          >
            Where to go from{" "}
            <em className="italic text-camel">here</em>.
          </h2>

          <div
            className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-10"
            data-testid="thank-you-next-steps"
          >
            {STEPS.map((s, i) => (
              <article
                key={i}
                data-testid={`thank-you-step-${i + 1}`}
                className="bg-paper border border-paperdeep p-8 md:p-10 flex flex-col"
              >
                <p className="eyebrow mb-5">{s.label}</p>
                <h3
                  className="font-serif text-nearblack leading-[1.15]"
                  style={{ fontSize: "clamp(1.35rem, 2vw, 1.7rem)", fontWeight: 400 }}
                >
                  {s.title}
                </h3>
                <p className="mt-5 text-nearblack/75 text-[14.5px] leading-[1.85] font-light flex-1">
                  {s.body}
                </p>
                <a
                  href={s.href}
                  target={s.external ? "_blank" : undefined}
                  rel={s.external ? "noopener noreferrer" : undefined}
                  data-testid={`thank-you-step-${i + 1}-cta`}
                  className="mt-8 inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-nearblack hover:text-camel transition-colors group"
                >
                  {s.cta}
                  <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
              </article>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
